import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../Kit/_index';

const BLOCK = 'LStudio__app_button';

/*
    🔥 BOTÓN — EL QUE FALTABA

    Confirm, Form, Kit, Screen y Wizard definían cada uno su propio
    __button. Cinco botones parecidos pero distintos: bordes de un
    píxel de diferencia, alturas que no coinciden, hovers con timings
    propios. Este es el canónico.

    ⚠️ ES <button>, <a> O <Link> SEGÚN LO QUE HAGA

    Si navega, tiene que ser un enlace: se abre con ctrl+clic, se copia
    la dirección, el lector de pantalla dice "enlace". Un <button> con
    onClick={() => navigate(…)} rompe las tres cosas.

      to    -> <Link>  (rutas internas)
      href  -> <a>     (externas; suma rel="noreferrer" solo)
      nada  -> <button>

    ⚠️ isLoading DESHABILITA

    Un botón que muestra spinner pero acepta clics dispara la petición
    tres veces. El ancho se conserva para que la fila no salte.

    ⚠️ EL ÍCONO SOLO NECESITA aria-label

    Con isIconOnly no hay texto que leer. Si no pasás aria-label, en
    desarrollo avisa por consola en vez de fallar en silencio.
*/

export const AppButton = forwardRef(({
    children = null, variant = 'solid', // solid | soft | outline | ghost | link
    tone = 'accent', // neutral | accent | ok | warn | danger
    size = 'md', // sm | md | lg
    icon = null, iconEnd = null, isIconOnly = false, isLoading = false, isDisabled = false,
    isFullWidth = false, type = 'button', to = null, href = null, className = '', ...rest
}, ref) => {

    const IS_BLOCKED = isDisabled || isLoading;

    if (process.env.NODE_ENV === 'development' && isIconOnly && !rest['aria-label']) {
        console.warn('[AppButton] isIconOnly sin aria-label: el botón queda mudo para un lector de pantalla.');
    }

    const PROPS = {
        ref, className: `${BLOCK} ${className}`.trim(),
        'data-variant': variant, 'data-tone': tone,
        'data-size': size, 'data-icon-only': isIconOnly,
        'data-loading': isLoading, 'data-full': isFullWidth, ...rest,
    };

    const CONTENT = (
        <>
            {
                isLoading
                    ? <span className={`${BLOCK}__spin`}><Spinner size={size === 'sm' ? 12 : 15} isInline /></span>
                    : icon && <span className={`${BLOCK}__icon`} aria-hidden="true">{icon}</span>
            }

            {!isIconOnly && children && <span className={`${BLOCK}__label`}>{children}</span>}

            {!isLoading && iconEnd && <span className={`${BLOCK}__icon`} aria-hidden="true">{iconEnd}</span>}
        </>
    );

    // -------------------- 🎯 RUTA INTERNA
    if (to && !IS_BLOCKED) {
        return <Link {...PROPS} to={to}>{CONTENT}</Link>;
    }

    // -------------------- 🎯 ENLACE EXTERNO
    if (href && !IS_BLOCKED) {
        return (
            <a {...PROPS} href={href}
                target={rest.target ?? '_blank'}
                rel={rest.target === '_self' ? undefined : 'noreferrer noopener'} >
                {CONTENT}
            </a>
        );
    }

    /*
        🔥 Un enlace deshabilitado no existe en HTML: si to/href están
           bloqueados, cae a <button disabled>, que sí se puede apagar.
    */
    return (
        <button {...PROPS} type={type} disabled={IS_BLOCKED} aria-busy={isLoading}>
            {CONTENT}
        </button>
    );
});

AppButton.displayName = 'AppButton';

/*
    🔥 GRUPO DE BOTONES

    Pega los botones entre sí y redondea solo los extremos. Para
    barras de herramientas y para el par "guardar / cancelar".
*/
export const ButtonGroup = ({ isAttached = false, size = 'md', children }) => (
    <div className={`${BLOCK}_group`} data-attached={isAttached} data-size={size} role="group">
        {children}
    </div>
);
